import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, CheckCircle } from 'lucide-react';
import { useSettings } from '@/hooks/useProducts';
import { useAuthStore } from '@/stores/auth-store';
import api from '@/lib/api';
import Button from '@/components/ui/Button';

export default function BackInStockButton({ variantId }: { variantId: string }) {
  const { t } = useTranslation('upsell');
  const { data } = useSettings();
  const { user } = useAuthStore();
  const [email, setEmail] = useState(user?.email || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [showInput, setShowInput] = useState(false);

  const config = data?.settings?.upsell_config?.back_in_stock;
  if (!config?.enabled) return null;

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    if (!email.trim()) return;

    setIsSubmitting(true);
    try {
      await api.post('/back-in-stock/subscribe', { email: email.trim(), variant_id: variantId });
      setIsSubscribed(true);
    } catch {
      // Silent fail
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isSubscribed) {
    return (
      <div className="flex items-center gap-2 text-sm text-success-700 py-2">
        <CheckCircle className="h-4 w-4" aria-hidden="true" />
        <span>{t('backInStock.subscribed')}</span>
      </div>
    );
  }

  if (user?.email && !showInput) {
    return (
      <Button
        variant="outline"
        onClick={() => { setEmail(user.email); handleSubmit(); }}
        isLoading={isSubmitting}
        className="w-full"
      >
        <Bell className="h-4 w-4" aria-hidden="true" />
        {config.button_text || t('backInStock.defaultButtonText')}
      </Button>
    );
  }

  if (!showInput) {
    return (
      <Button variant="outline" onClick={() => setShowInput(true)} className="w-full">
        <Bell className="h-4 w-4" aria-hidden="true" />
        {config.button_text || t('backInStock.defaultButtonText')}
      </Button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex gap-2">
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t('backInStock.emailPlaceholder')}
        required
        className="flex-1 px-3 py-2 border border-line-strong rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sage-500"
      />
      <Button type="submit" size="sm" isLoading={isSubmitting}>
        {t('backInStock.notifyMe')}
      </Button>
    </form>
  );
}
